/**
 * Sentry/GlitchTip PII scrubbing for the browser SDK.
 *
 * Mirror of the backend's `before_send`
 * (vidario-backend/main/sentry_helpers.py). Vidario processes
 * GDPR-sensitive recruiting data (candidate names, emails, CVs, free-text
 * notes). A default Sentry browser event can carry that data in request
 * bodies, headers, breadcrumb payloads, and logged messages. `scrubEvent`
 * runs in `beforeSend` and strips it before the event leaves the browser.
 *
 * Allow-list approach (not deny-list): only fields we explicitly keep
 * survive — request method/path, an allow-list of safe headers, user
 * id/role, and the exception itself. Everything else is dropped or masked.
 * Keep this in lockstep with the backend module: adding a sensitive field
 * means extending the allow-list/regex here *and* a test case.
 */
import type { Breadcrumb, ErrorEvent } from "@sentry/react";

export const MASK = "[scrubbed]";

/** Headers we keep; everything else (Authorization, Cookie, X-CSRFToken,
 * …) is dropped. Lower-cased for case-insensitive matching. */
const ALLOWED_HEADERS = new Set([
  "host",
  "user-agent",
  "accept",
  "accept-language",
  "content-type",
  "content-length",
  "referer",
  "origin",
]);

/** User fields we keep — id + role only. No email, username, or name. */
const ALLOWED_USER_KEYS = new Set(["id", "role", "ip_address"]);

/** Breadcrumb `data` keys that carry request/response payloads — dropped
 * wholesale (they regularly contain PII). */
const BREADCRUMB_PAYLOAD_KEYS = [
  "data",
  "body",
  "json",
  "form",
  "params",
  "request_body",
  "response_body",
];

/** Keys whose *values* look PII-shaped get masked. Mirrors the backend
 * regex. The browser SDK rarely populates stack-frame locals (`vars`),
 * but we scrub them defensively so a future SDK change can't leak. */
const SENSITIVE_KEY =
  /^(password|token|secret|api_?key|email|cookie|authorization|otp|name|first_?name|last_?name|company|phone|notes?|cv|photo|internal_notes|interview_notes|recruiter_notes|recommendation_text|description|address)$/i;

/** Drop the query string from a URL — it can carry `?email=…&token=…`. */
function withoutQuery(url: string): string {
  const q = url.indexOf("?");
  return q === -1 ? url : url.slice(0, q);
}

/** Email-shaped substring matcher — the most common PII leak into free
 * text (exception messages, logged messages). */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Mask any email-shaped substring in free text, in place, so the rest of
 * the message stays useful for debugging. */
function maskPii(text: string): string {
  return text.replace(EMAIL_RE, MASK);
}

function scrubRequest(request: NonNullable<ErrorEvent["request"]>): void {
  // Hard-drop bodies + cookies — these regularly carry PII.
  delete request.data;
  delete request.cookies;

  if (request.query_string != null) {
    request.query_string = MASK;
  }
  if (typeof request.url === "string") {
    request.url = withoutQuery(request.url);
  }

  const headers = request.headers;
  if (headers && typeof headers === "object" && !Array.isArray(headers)) {
    for (const key of Object.keys(headers)) {
      if (!ALLOWED_HEADERS.has(key.toLowerCase())) {
        delete headers[key];
      }
    }
  }
}

function scrubUser(user: NonNullable<ErrorEvent["user"]>): void {
  for (const key of Object.keys(user)) {
    if (!ALLOWED_USER_KEYS.has(key)) {
      delete user[key];
    }
  }
}

function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const data = crumb.data;
  if (data && typeof data === "object") {
    for (const key of BREADCRUMB_PAYLOAD_KEYS) {
      delete data[key];
    }
    for (const key of Object.keys(data)) {
      if (SENSITIVE_KEY.test(key)) {
        data[key] = MASK;
      } else if ((key === "url" || key === "to" || key === "from") && typeof data[key] === "string") {
        data[key] = withoutQuery(data[key] as string);
      }
    }
  }
  // Catches stray emails logged into a message, e.g. "user a@b.com did X".
  if (typeof crumb.message === "string" && crumb.message.includes("@")) {
    crumb.message = MASK;
  }
  return crumb;
}

function scrubException(exception: NonNullable<ErrorEvent["exception"]>): void {
  for (const value of exception.values ?? []) {
    // The exception message itself can carry PII, e.g. a backend error
    // surfaced as "User candidate@example.com not found". Mask email-shaped
    // substrings in place — masking the whole message would throw away the
    // error type/context that makes the report actionable.
    if (typeof value.value === "string") {
      value.value = maskPii(value.value);
    }
    for (const frame of value.stacktrace?.frames ?? []) {
      const vars = frame.vars;
      if (vars && typeof vars === "object") {
        for (const key of Object.keys(vars)) {
          if (SENSITIVE_KEY.test(key)) {
            vars[key] = MASK;
          }
        }
      }
    }
  }
}

/** Sentry `beforeSend` hook — scrub PII from every outgoing event.
 * Mutates and returns the event (Sentry's expected contract). */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    scrubRequest(event.request);
  }
  if (event.user) {
    scrubUser(event.user);
  }
  if (event.exception) {
    scrubException(event.exception);
  }
  // Top-level message (from captureMessage or a stringified error) can also
  // leak an email — mask substrings the same way as exception messages.
  if (typeof event.message === "string") {
    event.message = maskPii(event.message);
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  return event;
}
